/**
 * Kiosk Report PDF export.
 * Uses jsPDF to produce a clean, printable, text-based PDF of a single kiosk scan.
 * Keeps file size small (~15 kB) by rendering vector text — no html2canvas.
 */
import jsPDF from "jspdf";
import { VITAL_SPECS, CATEGORY_ORDER, CATEGORY_LABELS, statusOf, formatRange } from "./vitalSpecs";

const BRAND_RED = [230, 57, 70];
const BRAND_ORANGE = [247, 127, 0];
const INK = [17, 24, 39];
const MUTED = [107, 114, 128];
const GREEN = [16, 185, 129];
const AMBER = [217, 119, 6];

function statusColor(status) {
    if (status === "high") return BRAND_RED;
    if (status === "low") return [37, 99, 235];
    if (status === "normal") return GREEN;
    return MUTED;
}

function formatValue(spec, value) {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "number") return Number.isInteger(value) ? String(value) : Number(value).toFixed(1);
    return String(value);
}

/**
 * Build & download a PDF for a single kiosk scan.
 * @param {object} vitals — the flat metric map (matches KioskVitals API shape)
 * @param {object} patient — { name, email, age }
 */
export function exportKioskReportPdf(vitals, patient = {}) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 42;
    let y = 48;

    // ---------- Header ----------
    doc.setFillColor(...BRAND_RED);
    doc.rect(0, 0, pageW, 8, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...INK);
    doc.text("Medx AI · Kiosk Health Report", marginX, y);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    y += 16;
    doc.text("The world's first interactive AI digital health kiosk", marginX, y);

    // ---------- Patient / meta card ----------
    y += 24;
    doc.setDrawColor(230, 232, 238);
    doc.setFillColor(255, 247, 241);
    doc.roundedRect(marginX, y, pageW - marginX * 2, 74, 8, 8, "FD");

    const capturedAt = vitals?.captured_at ? new Date(vitals.captured_at).toLocaleString() : "—";
    const kioskId = vitals?.kiosk_id || "—";
    const rows = [
        ["Patient", patient?.name || "—"],
        ["Email", patient?.email || "—"],
        ["Age / Gender", `${patient?.age || vitals?.patient_age || "—"} · ${vitals?.gender || "—"}`],
        ["Kiosk", kioskId],
        ["Captured", capturedAt],
        ["Health Score", `${vitals?.health_score ?? "—"} / 100`],
    ];
    const cardTop = y + 14;
    const colW = (pageW - marginX * 2 - 20) / 3;
    rows.forEach((r, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const cx = marginX + 12 + col * colW;
        const cy = cardTop + row * 22;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.text(r[0].toUpperCase(), cx, cy);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...INK);
        doc.text(String(r[1]), cx, cy + 12);
    });

    y += 90;

    // ---------- HR triage flag ----------
    const flag = vitals?.hr_flag;
    if (flag && flag.code) {
        const isUrgent = flag.code === "urgent_leave";
        const isBreak = flag.code === "take_a_break";
        const fillC = isUrgent ? BRAND_RED : isBreak ? BRAND_ORANGE : GREEN;
        doc.setFillColor(...fillC);
        doc.roundedRect(marginX, y, pageW - marginX * 2, 32, 6, 6, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(`TRIAGE: ${flag.label || "OPTIMAL"}`, marginX + 12, y + 14);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const msg = doc.splitTextToSize(flag.message || "", pageW - marginX * 2 - 24);
        doc.text(msg, marginX + 12, y + 26);
        y += 44;
    }

    // ---------- Metric groups ----------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...INK);
    doc.text("35-Metric Vital Panel", marginX, y);
    y += 6;
    doc.setDrawColor(...BRAND_ORANGE);
    doc.setLineWidth(1.5);
    doc.line(marginX, y + 3, marginX + 46, y + 3);
    y += 18;

    for (const cat of CATEGORY_ORDER) {
        const specs = VITAL_SPECS.filter((s) => s.category === cat);
        if (specs.length === 0) continue;

        // Page break if needed
        if (y > pageH - 120) {
            doc.addPage();
            y = 48;
        }

        // Category heading
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(...BRAND_RED);
        doc.text(CATEGORY_LABELS[cat].toUpperCase(), marginX, y);
        y += 6;
        doc.setDrawColor(240, 240, 240);
        doc.setLineWidth(0.5);
        doc.line(marginX, y, pageW - marginX, y);
        y += 10;

        // Metric rows
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        for (const spec of specs) {
            if (y > pageH - 50) {
                doc.addPage();
                y = 48;
            }
            const value = vitals?.[spec.key];
            const status = statusOf(spec.key, value);
            const disp = formatValue(spec, value) + (spec.unit ? ` ${spec.unit}` : "");
            const range = "Normal: " + formatRange(spec) + (spec.unit ? ` ${spec.unit}` : "");
            const badgeText = status === "high" ? "HIGH" : status === "low" ? "LOW" : status === "normal" ? "OK" : "—";
            const badgeColor = statusColor(status);

            // Label
            doc.setTextColor(...INK);
            doc.setFont("helvetica", "normal");
            doc.text(spec.label, marginX + 4, y);
            // Range
            doc.setTextColor(...MUTED);
            doc.setFontSize(8);
            doc.text(range, marginX + 4, y + 11);
            doc.setFontSize(10);

            // Value (right-aligned before badge)
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...INK);
            const badgeW = 34;
            const valX = pageW - marginX - badgeW - 10;
            doc.text(disp, valX, y, { align: "right" });

            // Badge
            const badgeX = pageW - marginX - badgeW;
            doc.setFillColor(...badgeColor);
            doc.roundedRect(badgeX, y - 8, badgeW, 12, 3, 3, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(255, 255, 255);
            doc.text(badgeText, badgeX + badgeW / 2, y, { align: "center", baseline: "middle" });

            y += 18;
        }
        y += 6;
    }

    // ---------- Footer disclaimer ----------
    if (y > pageH - 90) {
        doc.addPage();
        y = 48;
    } else {
        y += 12;
    }
    doc.setDrawColor(255, 214, 194);
    doc.setFillColor(255, 247, 241);
    doc.roundedRect(marginX, y, pageW - marginX * 2, 58, 6, 6, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...AMBER);
    doc.text("DISCLAIMER", marginX + 12, y + 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    const disclaimer = "This report is a snapshot from an interactive kiosk scan — not a medical diagnosis. Consult a licensed physician for any clinical decisions. Do not use this report to self-medicate.";
    doc.text(doc.splitTextToSize(disclaimer, pageW - marginX * 2 - 24), marginX + 12, y + 30);
    y += 68;

    // ---------- Page footers (page number + brand) ----------
    const pageCount = doc.internal.pages.length - 1;
    for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.text("Medx AI · medxai.online", marginX, pageH - 20);
        doc.text(`Page ${p} of ${pageCount}`, pageW - marginX, pageH - 20, { align: "right" });
    }

    const safePatient = (patient?.name || "patient").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const stamp = vitals?.captured_at ? new Date(vitals.captured_at).toISOString().slice(0, 10) : "report";
    doc.save(`medxai-kiosk-report-${safePatient}-${stamp}.pdf`);
}
